import { useMemo, useState } from "react";
import { moduleRegistry } from "../module.registry";
import { CreatorReaStudio } from "./creator-rea-studio";
import { CreatorIcon, type CreatorIconName } from "./creator-icons";
import type { OmnisyniaClientModule } from "../module.types";
import "./creator-workspace.css";

type SectionId = "chat" | "agentes" | "conocimiento" | "herramientas";
const modulesBySection: Record<SectionId, string[]> = {
  chat: ["chat-omnisynia"], agentes: ["chat-omnisynia", "teacher"],
  conocimiento: ["teacher"], herramientas: []
};
const topMenu: readonly { id: SectionId; label: string; icon: CreatorIconName }[] = [
  {id:"chat",label:"CHAT",icon:"chat"},
  {id:"agentes",label:"AGENTES",icon:"agents"},
  {id:"conocimiento",label:"CONOCIMIENTO",icon:"knowledge"},
  {id:"herramientas",label:"HERRAMIENTAS",icon:"tools"}
];
type CreatorWorkspaceProps = {
  userId: string;
  initialSection?: "agentes";
  onExit: () => void;
  onOpenAccount: () => void;
};

/** The same authenticated creator UI is used in browser and the iOS native WKWebView. */
export function CreatorWorkspace({ userId, initialSection = "agentes", onExit, onOpenAccount }: CreatorWorkspaceProps) {
  const [section, setSection] = useState<SectionId>(initialSection);
  const [activeToolId, setActiveToolId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [idea, setIdea] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [search, setSearch] = useState("");

  const modules = useMemo(() => moduleRegistry.filter(module =>
    module.status === "available" && (section === "herramientas" || modulesBySection[section].includes(module.id))
  ), [section]);
  const searchResults = search.trim() ? topMenu.filter(item =>
    item.label.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()
      .includes(search.trim().normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase())) : [];
  const activeModule: OmnisyniaClientModule | undefined = moduleRegistry.find(module => module.id === activeToolId);
  const ActiveTool = activeModule?.Component;

  function showSection(next: SectionId) {
    if (next === "chat") { onExit(); return; }
    setSection(next); setActiveToolId(null); setNote(""); setMobileMenuOpen(false);
  }
  function navigate(next: SectionId) {
    setSearch("");
    showSection(next);
  }
  function openTool(id: string) {
    if (id === "chat-omnisynia") { onExit(); return; }
    const module = moduleRegistry.find(item => item.id === id && item.status === "available");
    if (!module) { setNote("Este motor no está registrado en el cliente actual."); return; }
    setActiveToolId(id);
    setSection("herramientas");
  }
  function savePlan() {
    if (!idea.trim()) { setNote("Escribe una idea para guardarla."); return; }
    try {
      localStorage.setItem("omnisynia:creator:plan:" + userId,
        JSON.stringify({section,idea:idea.slice(0,2000),savedAt:new Date().toISOString()}));
      setNote("Plan de trabajo guardado en este dispositivo.");
    } catch { setNote("No se pudo guardar el plan."); }
  }
  function goToAccount() { setMobileMenuOpen(false); onOpenAccount(); }

  return <section className="cw-shell" aria-label="Creador — ONE CORE"
    onKeyDown={event => { if (event.key === "Escape") {setMobileMenuOpen(false);setSearch("");} }}>
    {mobileMenuOpen && <button className="cw-mobile-backdrop" type="button" aria-label="Cerrar menú del creador"
      onClick={() => setMobileMenuOpen(false)} />}
    <aside id="cw-creator-sidebar" className={mobileMenuOpen ? "cw-sidebar mobile-open" : "cw-sidebar"} aria-label="Menú del creador">
      <div className="cw-brand">
        <img src="/omnisynia-logo-official-web-v2.png" alt="Logo oficial OMNISYNIA" />
        <strong>OMNISYNIA</strong><small>ONE INTELLIGENCE</small>
        <button className="cw-close-mobile" type="button" onClick={() => setMobileMenuOpen(false)}
          aria-label="Cerrar navegación"><CreatorIcon name="close" size={22}/></button>
      </div>
      <nav className="cw-side-menu" aria-label="Secciones Creador">
        <button type="button" onClick={onExit}><CreatorIcon name="home"/><span>Inicio</span></button>
        <button type="button" className="cw-selected-root" onClick={() => showSection("agentes")}
          aria-expanded="true"><CreatorIcon name="core"/><span>Creador</span><CreatorIcon name="chevron" size={14}/></button>
        <div className="cw-creator-submenu">
          <button type="button" onClick={onExit}><CreatorIcon name="core"/><span>ONE CORE · Chat</span></button>
        </div>
        <div className="cw-menu-divider"/>
        <button type="button" onClick={() => showSection("chat")}><CreatorIcon name="chat"/><span>Chat</span></button>
        <button type="button" onClick={() => showSection("agentes")}><CreatorIcon name="agents"/><span>Agentes</span></button>
        <button type="button" onClick={() => showSection("conocimiento")}><CreatorIcon name="knowledge"/><span>Conocimiento</span></button>
        <button type="button" onClick={() => showSection("herramientas")}><CreatorIcon name="tools"/><span>Herramientas</span></button>
        <div className="cw-menu-divider"/>
        <button type="button" disabled title="La comunidad aún no está disponible en ONE CORE">
          <CreatorIcon name="community"/><span>Comunidad (pendiente)</span></button>
        <button type="button" disabled title="La academia aún no está disponible en ONE CORE">
          <CreatorIcon name="academy"/><span>Academia (pendiente)</span></button>
        <button type="button" onClick={goToAccount}><CreatorIcon name="billing"/><span>Facturación y cuenta</span></button>
        <div className="cw-menu-divider"/>
        <button type="button" onClick={goToAccount}><CreatorIcon name="account"/><span>Perfil y acceso</span></button>
        <button type="button" onClick={goToAccount}><CreatorIcon name="settings"/><span>Ajustes de cuenta</span></button>
      </nav>
      <div className="cw-sidebar-footer"><img src="/omnisynia-logo-official-web-v2.png" alt=""/>
        <strong>OMNISYNIA</strong><small>UNA INTELIGENCIA. INFINITAS POSIBILIDADES.</small></div>
    </aside>
    <div className="cw-main">
      <header className="cw-header">
        <button type="button" className="cw-open-mobile" onClick={() => setMobileMenuOpen(true)}
          aria-label="Abrir menú del creador" aria-expanded={mobileMenuOpen} aria-controls="cw-creator-sidebar">
          <CreatorIcon name="menu" size={23}/>
        </button>
        <div className="cw-heading"><h1>CREADOR <span>›</span> ONE CORE</h1>
          <p>TU ESPACIO DE CREACIÓN, SIN LÍMITES</p></div>
        <div className="cw-header-actions">
          <div className="cw-search" role="search">
            <CreatorIcon name="search" size={17}/>
            <input type="search" value={search} onChange={event=>setSearch(event.target.value)}
              placeholder="Buscar en OMNISYNIA..." aria-label="Buscar una sección de ONE CORE"
              autoComplete="off"/>
            {search.trim() && <div className="cw-search-results" aria-label="Coincidencias de módulos">
              {searchResults.length ? searchResults.map(item => <button type="button" key={item.id}
                onClick={() => navigate(item.id)}><CreatorIcon name={item.icon} size={16}/>{item.label}</button>)
                : <p>No hay módulos con ese nombre.</p>}
            </div>}
          </div>
          <button type="button" className="cw-account-button" onClick={goToAccount}
            title="Abrir perfil y acceso" aria-label="Abrir perfil y acceso"><CreatorIcon name="account" size={21}/></button>
          <span className="cw-headline">MÁS QUE IA <small>INTELIGENCIA PARA CREAR</small></span>
        </div>
      </header>
      <nav className="cw-top-menu" aria-label="Motores de ONE CORE">
        {topMenu.map(item => <button type="button" key={item.id} aria-pressed={section===item.id}
          className={section===item.id?"selected":""} onClick={()=>navigate(item.id)}>
          <CreatorIcon name={item.icon} size={19}/><span>{item.label}</span></button>)}
      </nav>
      <div className="cw-inner">
        {section === "agentes" ? <CreatorReaStudio key={userId + ":agents"}
          userId={userId} mode="agents"/> :
        <section className="cw-module-view">
          <div className="cw-module-header">
            <div><p>ONE CORE</p><h2>{topMenu.find(item=>item.id===section)?.label}</h2>
              <small>Accede a los módulos disponibles sin salir de Creador.</small></div>
            <button type="button" onClick={onExit}>← Volver al chat</button>
          </div>
          {activeToolId === "rea-onecore" ?
            <article className="cw-tool-view"><header><strong>REA · ONE CORE</strong>
              <button type="button" onClick={()=>setActiveToolId(null)}>Cerrar herramienta</button></header>
              <CreatorReaStudio key={userId + ":rea"} userId={userId}/></article> :
          ActiveTool ?
            <article className="cw-tool-view"><header><strong>{activeModule?.displayName}</strong>
              <button type="button" onClick={()=>setActiveToolId(null)}>Cerrar herramienta</button></header>
              <ActiveTool/></article> :
            <div className="cw-module-grid">
              <div className="cw-plan-card"><h3>Nuevo trabajo</h3><label htmlFor="cw-plan">Instrucciones</label>
                <textarea data-writing="literal" id="cw-plan" value={idea} maxLength={2000}
                  onChange={e=>setIdea(e.target.value)} placeholder="Escribe qué quieres desarrollar en ONE CORE..."/>
                <button type="button" onClick={savePlan}>Guardar plan</button>
                {note && <p role="status">{note}</p>}</div>
              <div className="cw-plan-card"><h3>Motores registrados</h3>
                <p>Se muestran únicamente módulos que existen en esta versión.</p>
                {section === "herramientas" && <button type="button" className="cw-tool-entry"
                  onClick={()=>setActiveToolId("rea-onecore")}><strong>REA · Reconstrucción de aplicaciones</strong>
                  <small>Analizar funciones documentadas y generar prototipos originales en ONE CORE.</small></button>}
                {modules.length ? modules.map(module => <button type="button" key={module.id}
                  onClick={()=>openTool(module.id)} className="cw-tool-entry"><strong>{module.displayName}</strong>
                  <small>{module.description}</small></button>) :
                  <p>Todavía no existe una pantalla conectada para esta sección.</p>}
              </div>
            </div>}
        </section>}
      </div>
      <footer className="cw-footer">OMNISYNIA — UNA INTELIGENCIA. INFINITAS POSIBILIDADES.</footer>
    </div>
  </section>;
}

